import { localeAtom } from '@repo/i18n';
import {
    ProductSearch,
    OctoSupplier,
    OctoProduct,
    OctoCalendarEntry,
    OctoCalendarRequest,
    OctoAvailabilityRequest,
    OctoAvailabilityEntry,
    OctoNextAvailabilityRequest,
    OctoNextAvailabilityResponse,
    OctoBookingReservationRequest,
    OctoBooking,
    OctoOrder,
    OctoBookingConfirmRequest,
    OctoBookingPatchRequest,
    OctoBookingCancelRequest,
    OctoOrderCancelRequest,
    OctoOrderExtendRequest,
    OctoBookingUpdateRequest,
} from '@repo/types';
import { configurationAtom } from '../../widgetsConfiguration';
import { get, getRequest, patch, post, put } from '../utils/api-client-common';

export async function getOctoProduct(id: string) {
    const { locale } = localeAtom.subject.value;
    const config = configurationAtom.subject.value;
    const { url, headers } = getRequest(`/api/v1/octo/products/${id}`, locale, config);
    headers.append('Octo-Capabilities', 'octo/content,octo/pricing,octo/cart,octo/extras');

    return await get<OctoProduct>(url, headers);
}

export async function getOctoProducts(ids: string[]) {
    const { locale } = localeAtom.subject.value;
    const config = configurationAtom.subject.value;
    const { url, headers } = getRequest(`/api/v1/octo/products`, locale, config);
    headers.append('Octo-Capabilities', 'octo/content,octo/pricing,octo/extras');

    const products = await get<OctoProduct[]>(url, headers);
    return products.filter((p) => ids.includes(p.id));
}

export async function getOctoOrder(id: string) {
    const { locale } = localeAtom.subject.value;
    const config = configurationAtom.subject.value;
    const { url, headers } = getRequest(`/api/v1/octo/orders/${id}`, locale, config);
    headers.append(
        'Octo-Capabilities',
        'octo/content,octo/pricing,octo/cart,octo/extras,octo/cardPayments',
    );

    return await get<OctoOrder>(url, headers);
}

export async function getOctoProductSearch(_filters: ProductSearch) {
    const { locale } = localeAtom.subject.value;
    const config = configurationAtom.subject.value;

    // TODO: Implement filters

    const { url, headers } = getRequest(`/api/v1/octo/products`, locale, config);
    headers.append('Octo-Capabilities', 'octo/content,octo/pricing,octo/extras');

    return await get<OctoProduct[]>(url, headers);
}

export async function getOctoSupplier() {
    const { locale } = localeAtom.subject.value;
    const config = configurationAtom.subject.value;

    const { url, headers } = getRequest(
        `/api/v1/octo/suppliers/${config.octoSupplierId}`,
        locale,
        config,
    );
    headers.append('Octo-Capabilities', 'octo/content');

    return await get<OctoSupplier>(url, headers);
}

export async function getOctoCalendar(request: OctoCalendarRequest): Promise<OctoCalendarEntry[]> {
    const { locale } = localeAtom.subject.value;
    const config = configurationAtom.subject.value;

    const { url, headers } = getRequest(`/api/v1/octo/availability/calendar`, locale, config);

    // Only include pricing/extras capabilities when units are provided (OCTO API requirement)
    const hasUnits = request.units && request.units.length > 0;
    if (hasUnits) {
        headers.append('Octo-Capabilities', 'octo/pricing,octo/extras');
    }

    return await post(url, request, headers);
}

export async function getOctoAvailability(
    request: OctoAvailabilityRequest,
): Promise<OctoAvailabilityEntry[]> {
    const { locale } = localeAtom.subject.value;
    const config = configurationAtom.subject.value;

    const { url, headers } = getRequest(`/api/v1/octo/availability`, locale, config);

    // Only include pricing/extras capabilities when units are provided (OCTO API requirement)
    const hasUnits = request.units && request.units.length > 0;
    if (hasUnits) {
        headers.append('Octo-Capabilities', 'octo/pricing,octo/extras');
    }

    return await post(url, request, headers);
}

export async function getOctoNextAvailability(
    request: OctoNextAvailabilityRequest,
): Promise<OctoNextAvailabilityResponse> {
    const { locale } = localeAtom.subject.value;
    const config = configurationAtom.subject.value;

    const { url, headers } = getRequest(`/api/v1/octo/availability/next`, locale, config);

    return await post(url, request, headers);
}

export async function createOctoBooking(
    request: OctoBookingReservationRequest,
): Promise<OctoBooking> {
    const { locale } = localeAtom.subject.value;
    const config = configurationAtom.subject.value;
    const { url, headers } = getRequest(`/api/v1/octo/bookings`, locale, config);
    headers.append(
        'Octo-Capabilities',
        'octo/content,octo/pricing,octo/cart,octo/extras,octo/cardPayments',
    );

    return await post(url, request, headers);
}

export async function patchOctoOrder(
    orderId: string,
    request: OctoBookingPatchRequest,
): Promise<OctoOrder> {
    const { locale } = localeAtom.subject.value;
    const config = configurationAtom.subject.value;
    const { url, headers } = getRequest(`/api/v1/octo/orders/${orderId}`, locale, config);
    headers.append(
        'Octo-Capabilities',
        'octo/content,octo/pricing,octo/cart,octo/extras,octo/cardPayments',
    );

    return await patch(url, request, headers);
}

export async function confirmOctoOrder(
    orderId: string,
    request: OctoBookingConfirmRequest,
): Promise<OctoOrder> {
    const { locale } = localeAtom.subject.value;
    const config = configurationAtom.subject.value;
    const { url, headers } = getRequest(`/api/v1/octo/orders/${orderId}/confirm`, locale, config);
    headers.append(
        'Octo-Capabilities',
        'octo/content,octo/pricing,octo/cart,octo/extras,octo/cardPayments',
    );

    return await post(url, request, headers);
}

export async function cancelOctoOrder(
    orderId: string,
    request: OctoOrderCancelRequest,
): Promise<OctoOrder> {
    const { locale } = localeAtom.subject.value;
    const config = configurationAtom.subject.value;
    const { url, headers } = getRequest(`/api/v1/octo/orders/${orderId}/cancel`, locale, config);
    headers.append(
        'Octo-Capabilities',
        'octo/content,octo/pricing,octo/cart,octo/extras,octo/cardPayments',
    );

    const timeout = undefined;
    const parseJsonResponse = false;
    return await post(url, request, headers, timeout, parseJsonResponse);
}

export async function extendOctoOrder(
    orderId: string,
    request: OctoOrderExtendRequest,
): Promise<OctoOrder> {
    const { locale } = localeAtom.subject.value;
    const config = configurationAtom.subject.value;
    const { url, headers } = getRequest(`/api/v1/octo/orders/${orderId}/extend`, locale, config);
    headers.append(
        'Octo-Capabilities',
        'octo/content,octo/pricing,octo/cart,octo/extras,octo/cardPayments',
    );

    return await post(url, request, headers);
}

export async function cancelOctoBooking(
    bookingId: string,
    request: OctoBookingCancelRequest,
): Promise<OctoOrder> {
    const { locale } = localeAtom.subject.value;
    const config = configurationAtom.subject.value;
    const { url, headers } = getRequest(
        `/api/v1/octo/bookings/${bookingId}/cancel`,
        locale,
        config,
    );
    headers.append(
        'Octo-Capabilities',
        'octo/content,octo/pricing,octo/cart,octo/extras,octo/cardPayments',
    );

    const timeout = undefined;
    const parseJsonResponse = false;
    return await post(url, request, headers, timeout, parseJsonResponse);
}

export async function updateOctoBooking(
    bookingId: string,
    request: OctoBookingUpdateRequest,
): Promise<OctoBooking> {
    const { locale } = localeAtom.subject.value;
    const config = configurationAtom.subject.value;
    const { url, headers } = getRequest(`/api/v1/octo/bookings/${bookingId}`, locale, config);
    headers.append(
        'Octo-Capabilities',
        'octo/content,octo/pricing,octo/cart,octo/extras,octo/cardPayments',
    );

    return await put(url, request, headers);
}
